import { useEffect, useState } from "react";
import { Package, Plus, Pencil, Trash2, X, DollarSign, NotebookPen, Tag, Save, Lock } from "lucide-react";
import { getProducts, saveProduct, deleteProduct, SAMPLE_PRODUCTS } from "../lib/products";
import { formatCurrency } from "../lib/storage";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";
import { toast } from "sonner";

const emptyProduct = {
  id: null,
  name: "",
  category: "",
  costPerItem: "",
  defaultSalePrice: "",
  notes: "",
};

export default function Products() {
  const { pro, openUpgrade } = useProState();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = () => setProducts(getProducts());
  useEffect(() => { refresh(); }, []);

  const onSave = (data) => {
    if (!data.name?.trim()) { toast.error("Please enter a product name"); return; }
    saveProduct(data);
    toast.success(data.id ? "Product updated" : "Product saved");
    setEditing(null);
    refresh();
  };

  const onDelete = (id) => {
    const p = products.find((x) => x.id === id);
    toast(`Delete "${p?.name || "this product"}"?`, {
      description: "Removes it from your library. Past event records keep their data.",
      duration: 6000,
      action: {
        label: "Delete",
        onClick: () => { deleteProduct(id); toast.success("Product deleted"); refresh(); },
      },
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="products-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <p className="label-eyebrow">Product Library</p>
            <ProBadge />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Your saved products
          </h1>
          <p className="text-brand-mute text-sm mt-1.5 max-w-xl">
            Save the products you sell once, then reuse them across every event. Track unit cost, sale price, and per-event sales.
          </p>
        </div>
        {pro ? (
          <button onClick={() => setEditing({ ...emptyProduct })} className="pop-btn-primary" data-testid="products-add-btn">
            <Plus className="w-4 h-4" /> Add product
          </button>
        ) : (
          <UpgradeButton variant="primary" label="Unlock — $9/month" testid="products-upgrade-btn" />
        )}
      </div>

      {/* FREE preview */}
      {!pro && (
        <>
          <div className="pop-card p-6 mb-6 flex items-start gap-4 bg-brand-gold/5 border-brand-gold/30" data-testid="products-paywall">
            <span className="w-11 h-11 rounded-2xl bg-brand-gold/15 text-brand-gold grid place-items-center shrink-0">
              <Lock className="w-5 h-5" strokeWidth={2} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg font-semibold text-brand-ink">Product Library is a Pro feature</p>
              <p className="text-sm text-brand-mute mt-1">
                Save unlimited products, assign them to events, and see which ones drive the most profit.
                Below is a preview of how it'll look once you unlock it.
              </p>
              <div className="mt-4">
                <UpgradeButton variant="primary" label="Subscribe — $9/month" testid="products-paywall-upgrade" />
              </div>
            </div>
          </div>
          <p className="label-eyebrow mb-3">Preview</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75 pointer-events-none select-none" data-testid="products-sample-grid">
            {SAMPLE_PRODUCTS.map((p) => (
              <ProductCard key={p.id} product={p} sample />
            ))}
          </div>
          <button onClick={openUpgrade} className="mt-6 mx-auto block text-sm font-medium text-brand-green hover:underline">
            Unlock the Product Library →
          </button>
        </>
      )}

      {/* PRO library */}
      {pro && (
        products.length === 0 ? (
          <div className="pop-card p-10 text-center" data-testid="products-empty">
            <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brand-green/10 text-brand-green">
              <Package className="w-7 h-7" strokeWidth={2} />
            </span>
            <h3 className="font-heading text-xl font-semibold text-brand-ink mt-4">No products yet</h3>
            <p className="text-brand-mute text-sm mt-1.5 max-w-md mx-auto">
              Add your first product — the things you sell at markets. Soaps, candles, prints, baked goods, whatever you make.
            </p>
            <button onClick={() => setEditing({ ...emptyProduct })} className="pop-btn-primary mt-5" data-testid="products-empty-add">
              <Plus className="w-4 h-4" /> Add your first product
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onEdit={() => setEditing(p)}
                onDelete={() => onDelete(p.id)}
              />
            ))}
          </div>
        )
      )}

      {editing && (
        <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={onSave} />
      )}
    </div>
  );
}

function ProductCard({ product, onEdit, onDelete, sample = false }) {
  const cost = +product.costPerItem || 0;
  const price = +product.defaultSalePrice || 0;
  const margin = price > 0 ? ((price - cost) / price) * 100 : 0;
  return (
    <article className="pop-card pop-card-hover p-5 flex flex-col" data-testid={sample ? `sample-product-${product.id}` : `product-card-${product.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg font-semibold text-brand-ink truncate">{product.name}</h3>
          {product.category && (
            <p className="text-xs text-brand-mute mt-1 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" strokeWidth={2} /> {product.category}
            </p>
          )}
        </div>
        {!sample && (
          <div className="flex gap-1.5 shrink-0">
            <button onClick={onEdit} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-cream-soft text-brand-mute hover:text-brand-green transition-colors" data-testid={`product-edit-${product.id}`} aria-label="Edit">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={onDelete} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-error/10 text-brand-mute hover:text-brand-error transition-colors" data-testid={`product-delete-${product.id}`} aria-label="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
        {sample && (
          <span className="pop-pill bg-brand-cream-soft text-brand-mute border border-brand-line">Sample</span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-brand-cream-soft p-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">Cost / item</p>
          <p className="font-heading text-base font-semibold text-brand-ink mt-1 tabular-nums">{formatCurrency(cost)}</p>
        </div>
        <div className="rounded-xl bg-brand-green/5 border border-brand-green/15 p-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">Sale price</p>
          <p className="font-heading text-base font-semibold text-brand-green mt-1 tabular-nums">{formatCurrency(price)}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-brand-mute">Margin per item</span>
        <span className="font-semibold text-brand-ink tabular-nums">{margin.toFixed(1)}%</span>
      </div>

      {product.notes && (
        <p className="mt-4 pt-4 border-t border-brand-line text-xs text-brand-mute line-clamp-2">{product.notes}</p>
      )}
    </article>
  );
}

function ProductForm({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-brand-ink/40 backdrop-blur-sm p-0 sm:p-4 animate-fade-up" data-testid="product-form-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92dvh] overflow-y-auto shadow-2xl border border-brand-line">
        <div className="sticky top-0 bg-white/95 backdrop-blur z-10 flex items-center justify-between p-5 border-b border-brand-line">
          <div>
            <p className="label-eyebrow">{form.id ? "Edit product" : "New product"}</p>
            <h3 className="font-heading text-xl font-semibold text-brand-ink mt-1">{form.name || "Untitled product"}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-cream-soft text-brand-mute" data-testid="product-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div>
            <label className="pop-label" htmlFor="product-name">Product name</label>
            <input id="product-name" data-testid="product-input-name" className="pop-input" value={form.name} onChange={set("name")} placeholder="Lavender Soap" />
          </div>
          <div>
            <label className="pop-label" htmlFor="product-category">Category</label>
            <input id="product-category" data-testid="product-input-category" className="pop-input" value={form.category} onChange={set("category")} placeholder="Soap, Candles, Pantry…" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="pop-label" htmlFor="product-cost">Cost per item</label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" />
                <input id="product-cost" data-testid="product-input-cost" type="number" inputMode="decimal" min="0" step="0.01" className="pop-input pl-11" value={form.costPerItem} onChange={set("costPerItem")} placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="pop-label" htmlFor="product-price">Default sale price</label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" />
                <input id="product-price" data-testid="product-input-price" type="number" inputMode="decimal" min="0" step="0.01" className="pop-input pl-11" value={form.defaultSalePrice} onChange={set("defaultSalePrice")} placeholder="0.00" />
              </div>
            </div>
          </div>
          <div>
            <label className="pop-label flex items-center gap-1.5" htmlFor="product-notes">
              <NotebookPen className="w-3.5 h-3.5 text-brand-mute" /> Notes <span className="text-brand-mute text-xs font-normal">(optional)</span>
            </label>
            <textarea id="product-notes" data-testid="product-input-notes" rows="3" className="pop-input resize-none" value={form.notes} onChange={set("notes")} placeholder="Bestseller in summer, lavender from local farm…" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-brand-line p-4 max-sm:pb-24 flex gap-3 justify-end">
          <button onClick={onClose} className="pop-btn-secondary" data-testid="product-form-cancel">Cancel</button>
          <button onClick={() => onSave(form)} className="pop-btn-primary" data-testid="product-form-save">
            <Save className="w-4 h-4" /> {form.id ? "Save changes" : "Save product"}
          </button>
        </div>
      </div>
    </div>
  );
}
